import {view, Badge, Divider, Action} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {
  widgetLinkAction,
  LightCard,
  TextRow,
} from '@stripe-internal/conversations-ui';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

export type ValueProps = {
  has_follow_up_case: boolean;
  sco_id?: string;
  subject?: string;
  last_modified_date?: number;
  case_creator?: string;
  support_center_link?: string;
  show_contact_support_options?: boolean;
};

const messages = defineMessages({
  CaseCardTitle: {
    id: 'mse_inline_data.merchant_follow_up_case.case_card_title',
    description: 'Title for the card displaying the follow-up case.',
    defaultMessage: 'Support case',
  },
  lastModifiedDateLabel: {
    id: 'mse_inline_data.merchant_follow_up_case.last_modified_date_label',
    description:
      'Label for displaying the last modified date of a follow-up case.',
    defaultMessage: 'Last updated',
  },
  caseIDLabel: {
    id: 'mse_inline_data.merchant_follow_up_case.caseID_label',
    description: 'Label for displaying the case ID of a follow-up case.',
    defaultMessage: 'Case ID',
  },
  statusLabel: {
    id: 'mse_inline_data.merchant_follow_up_case.status_label',
    description: 'Label for displaying the status of a follow-up case.',
    defaultMessage: 'Status',
  },
  openStatus: {
    id: 'mse_inline_data.merchant_follow_up_case.open_status',
    description: 'Status for an open case.',
    defaultMessage: 'Open',
  },
  openStatusDescription: {
    id: 'mse_inline_data.merchant_follow_up_case.open_status_description',
    description: 'Status description for an open case.',
    defaultMessage:
      'This case has been assigned to a support agent who is actively investigating.',
  },
});

export const MerchantFollowUpCase = (props: InlineDatumReactComponentProps) => {
  const {datum} = props;
  const value = datum.value as ValueProps;
  const intl = useIntl();
  const {
    has_follow_up_case: hasFollowUpCase,
    sco_id: caseID,
    last_modified_date: lastModifiedDate,
    case_creator: caseCreator,
    subject,
    support_center_link: supportCenterLink,
    show_contact_support_options: showContactSupportOptions,
  } = value;

  const afterInlineDataMessage = defineMessages({
    visitSupportCenter: {
      id: 'mse_inline_data.merchant_follow_up_case.after_inline_data_message',
      description: 'Tells the merchant to visit the support center.',
      defaultMessage: `To learn more about all your support cases, we recommend viewing them in your [Support center]({supportCenterLink}).`,
    },
    checkStatusOfDifferentCase: {
      id: 'mse_inline_data.merchant_follow_up_case.different_case',
      description: 'Asks the merchant if they have a different case.',
      defaultMessage:
        'Would you like to check the status of a different support case?',
    },
  });

  const localizedModifiedDate =
    lastModifiedDate &&
    intl.formatDate(new Date(lastModifiedDate * 1000), {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });

  const analytics = useAnalytics();

  analytics.viewed('uh_merchant_follow_up_case', {
    hasFollowUpCase,
  });

  if (!hasFollowUpCase) {
    return null;
  }

  const ctaLink = `${supportCenterLink}/${caseID}`;

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <Action
        href={ctaLink}
        css={{
          textColor: 'text',
        }}
        disabled={!caseID}
        uses={[widgetLinkAction()]}
        onPress={(_) =>
          analytics.action('uh_merchant_follow_up_case_cta_button', {
            ctaLink,
          })
        }
      >
        <LightCard hover isOuter>
          <view.div css={{stack: 'y', gap: 'small'}}>
            <view.div css={{stack: 'x', gap: 'small'}}>
              <view.div
                css={{
                  stack: 'x',
                  gap: 'small',
                  font: 'label.small.emphasized',
                }}
              >
                <FormattedMessage {...messages.CaseCardTitle} />
                {caseCreator ? (
                  <>
                    <Divider />
                    <view.div
                      data-testid="case-creator"
                      css={{textColor: 'action.primary'}}
                    >
                      {caseCreator}
                    </view.div>
                  </>
                ) : null}
              </view.div>
              <Badge data-testid="status-badge" type="info">
                <FormattedMessage {...messages.openStatus} />
              </Badge>
            </view.div>
            <view.div css={{stack: 'y', gap: 'small'}}>
              <view.div
                data-testid="case-subject"
                css={{font: 'heading.small'}}
              >
                {subject}
              </view.div>
              <view.div css={{stack: 'x', gap: 'small'}}>
                <view.div css={{stack: 'y'}}>
                  <view.div css={{font: 'label.medium.emphasized'}}>
                    <FormattedMessage {...messages.caseIDLabel} />
                  </view.div>
                  <view.div
                    data-testid="case-id"
                    css={{font: 'label.medium', textColor: 'subdued'}}
                  >
                    {caseID}
                  </view.div>
                </view.div>
                {lastModifiedDate ? (
                  <view.div css={{stack: 'y'}}>
                    <view.div css={{font: 'label.medium.emphasized'}}>
                      <FormattedMessage {...messages.lastModifiedDateLabel} />
                    </view.div>
                    <view.div
                      data-testid="last-modified-date"
                      css={{font: 'label.medium', textColor: 'subdued'}}
                    >
                      {localizedModifiedDate}
                    </view.div>
                  </view.div>
                ) : null}
              </view.div>
              <view.div
                data-testid="status-description"
                css={{font: 'label.small', color: 'text.subdued'}}
              >
                <FormattedMessage {...messages.openStatusDescription} />
              </view.div>
            </view.div>
          </view.div>
        </LightCard>
      </Action>

      {!showContactSupportOptions && (
        <>
          <TextRow
            // eslint-disable-next-line react/style-prop-object
            style="none"
            body={intl.formatMessage(
              afterInlineDataMessage.visitSupportCenter,
              {
                supportCenterLink,
              },
            )}
          />
          <TextRow
            // eslint-disable-next-line react/style-prop-object
            style="none"
            body={intl.formatMessage(
              afterInlineDataMessage.checkStatusOfDifferentCase,
            )}
          />
        </>
      )}
    </view.div>
  );
};
